<template>
  <div class="text-light-950" v-if="post?.attachments?.poll">
   <TransitionGroup name="list-simple" tag="div" class="space-y-1 px-3 relative" ref="$container">
     <div
       class="relative font-medium flex items-center border border-charcoal-200 dark:border-charcoal-900 cursor-pointer"
       :class="[
         isVoteLoading ? 'pointer-events-none opacity-50' : '',
         hasVoted ? 'rounded-full h-10 px-4 bg-white dark:bg-dark-950' : 'rounded-lg bg-white dark:bg-dark-950 py-2 px-3',
         option.voters.includes(currentUser?._id ?? '') ? 'border-charcoal-950 dark:border-charcoal-100' : ''
       ]" v-for="(option, i) in post.attachments.poll.options.sort((a, b) => hasVoted ? b.voters.length - a.voters.length : 0)"
       @click="() => onPostVote(option._id)"
       :key="option._id"
     >
       <div class="flex items-center pointer-events-none grow space-x-2" :class="[ hasVoted ? 'absolute top-0 left-0 right-0 bottom-0 px-4' : '', (option.voters.length > 0 || option.voters.includes(currentUser?._id ?? '')) || !hasVoted ? 'text-light-950 dark:text-dark-100' : 'text-charcoal-600' ]" :style="hasVoted ? { width: `${width}px` } : {}">
         <div class="grow break-all" :class="[ hasVoted ? 'line-clamp-1' : '']">{{ option.label }}</div>
         <div v-if="option.voters.length > 0 && hasVoted">{{ Math.round((option.voters.length / totalVoters.length) * 100) }}%</div>
       </div>

       <div class="absolute m-0.5 top-0 right-0 left-0 bottom-0" v-if="hasVoted">
         <div class="absolute top-0 left-0 bottom-0 rounded-full bg-charcoal-300 bg-center bg-cover" :style="{ width: `${Math.round((option.voters.length / totalVoters.length) * 100)}%`, backgroundImage: `url(${gradientSupporter})` }" />
       </div>
       
       <div class="absolute top-0 left-0 right-0 bottom-0 pointer-events-none grow space-x-2 text-white overflow-hidden" :style="{ width: `${Math.round((option.voters.length / totalVoters.length) * 100)}%` }" v-if="hasVoted">
         <div class="flex items-center px-4 h-full" :style="{ width: `${width}px` }">
           <div class="grow break-all" :class="[ hasVoted ? 'line-clamp-1' : '']">{{ option.label }}</div>
           <div v-if="option.voters.length > 0 && hasVoted">{{ Math.round((option.voters.length / totalVoters.length) * 100) }}%</div>
         </div>
       </div>
     </div>
   </TransitionGroup>

   <div class="py-1.5 p-2.5 mt-3 text-xs bg-light-200 font-medium flex items-center space-x-2 dark:bg-dark-700 dark:text-dark-50">
     <div class="grow">
       <ButtonLink size="xs" @click="isShowVoters = true" v-if="hasVoted || post.owner === currentUser?._id">{{ totalVoters.length }} vote{{ $plu(totalVoters.length) }}</ButtonLink>
       <div v-else>{{ $t('components.Post.poll.voteCount', totalVoters.length) }}</div>
     </div>

     <ButtonLink size="xs" @click="() => onPostVote('remove')" v-if="hasVoted">
        {{ $t('components.Post.poll.remove') }}
     </ButtonLink>
   </div>

   <PopinBase size="2xs" :z="211" :is-active="isShowVoters" @close="isShowVoters = false">
     <template #body>
       <div class="p-6 space-y-2">
         <div class="px-4 py-3 border border-charcoal-200 rounded-md" v-for="(option, i) in post.attachments.poll.options">
           <p class="font-bold">{{ option.label }} : {{ option.voters.length }} vote{{ $plu(option.voters.length) }}</p>

           <div class="flex flex-wrap -mx-1 mt-2">
             <UserPicture
               v-for="user in getVoters(option._id)"
               class="m-1"
               size="sm"
               is-link
               :user="user"
             />
           </div>
         </div>
       </div>
     </template>
   </PopinBase>
 </div>
</template>

<script lang="ts" setup>
import gradientSupporter from '@/assets/img/gradients/hard/gradient-3.jpg'

import UserStore from '@/store/user'
import PostsStore from '@/store/posts'
import { Post, User } from '@/classes'

const props = withDefaults(defineProps<{
 post: Post
}>(), {
})

const postsStore = PostsStore()
const userStore = UserStore()
const { currentUser } = storeToRefs(userStore)

const isShowVoters = ref(false)

const $container = ref()
const { width } = useElementSize($container)

const totalVoters =  computed(() => {
 return (props.post?.attachments?.poll?.options ?? []).reduce((total: string[], option) => {

   return [ ...total, ...option.voters ]
 }, [])
})

const hasVoted = computed(() => totalVoters.value.includes(currentUser.value?._id ?? ''))
const isVoteLoading = ref(false)

const onPostVote = async (optionId: string) => {
 if (!props.post || !props.post.attachments?.poll) return

 isVoteLoading.value = true

 await postsStore.votePost(props.post._id, optionId)

 isVoteLoading.value = false
}

const { fetchUsersByIds } = useDataFetch()

const { result: users, refresh } = await fetchUsersByIds(
 Math.random().toString(),
 computed(() => totalVoters.value),
 undefined, {
   immediate: false
 }
)

watch(isShowVoters, () => {
 if (isShowVoters.value) refresh()
})

const getVoters = (optionId: string) => {

 return (props.post?.attachments?.poll?.options ?? []).find(option => option._id === optionId)?.voters.map(v => users.value.find(u => u._id === v)).filter(u => u) as User[]
}
</script>